import React, {useCallback, useEffect, useState, useRef} from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Dimensions,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Button,
  Chip,
  Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useSubscriptionStore} from '../../store/useSubscriptionStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const {width: SCREEN_WIDTH} = Dimensions.get('window');
const CARD_WIDTH = SCREEN_WIDTH * 0.78;
const CARD_MARGIN = spacing.sm;

const FEATURE_ICONS = {
  USER_LIMIT: 'account-group',
  CUSTOMER_LIMIT: 'account-multiple',
  ORDER_MONTH_LIMIT: 'clipboard-list',
  CUSTOMERS: 'account-heart',
  MEASUREMENTS: 'ruler',
  STITCH_OPTIONS: 'needle',
  GALLERY: 'image-multiple',
  DASHBOARD: 'view-dashboard',
  RATE_CARD: 'currency-inr',
  BILLS: 'receipt',
  WEB_APPLICATION: 'web',
  WORKERS: 'account-hard-hat',
  REPORTS: 'chart-bar',
  WEB_WHATSAPP: 'whatsapp',
  TAX: 'percent-outline',
  ADVANCED_REPORTS: 'chart-areaspline',
  EXPENSES: 'cash-minus',
  AUTOMATIC_WHATSAPP: 'message-flash',
  INVENTORY: 'warehouse',
  PREMIUM_SUPPORT: 'headset',
  FREE_UPGRADES: 'arrow-up-bold-circle',
  STANDARD_SUPPORT: 'lifebuoy',
};

// Duration key for matching variants
const DURATION_OPTIONS = [
  {key: 'M1', durationCount: 1, durationUnit: 'MONTH'},
  {key: 'M6', durationCount: 6, durationUnit: 'MONTH'},
  {key: 'Y1', durationCount: 12, durationUnit: 'MONTH'},
];

const PlanSelectionScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {orgUnitId, orgUnitName, subscriptionId} = route.params || {};

  const {plans, isLoading, fetchPlans} = useSubscriptionStore();
  const [selectedFamilyIndex, setSelectedFamilyIndex] = useState(0);
  const [selectedDuration, setSelectedDuration] = useState('Y1');
  const [snackbar, setSnackbar] = useState('');
  const flatListRef = useRef(null);

  // Filter out trial plans - only show paid plans
  const paidPlans = plans.filter(p => p.planType === 'PAID');

  useEffect(() => {
    fetchPlans().catch(e =>
      setSnackbar(e.response?.data?.message || t('common.error')),
    );
  }, [fetchPlans, t]);

  // Auto-scroll to PRO plan on first load
  useEffect(() => {
    if (paidPlans.length > 0 && flatListRef.current) {
      const proIndex = paidPlans.findIndex(p => p.code === 'PRO');
      if (proIndex >= 0) {
        setSelectedFamilyIndex(proIndex);
        setTimeout(() => {
          flatListRef.current?.scrollToIndex({index: proIndex, animated: false});
        }, 100);
      }
    }
  }, [paidPlans.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const getVariantForDuration = useCallback(
    (family, durationKey) => {
      const duration = DURATION_OPTIONS.find(d => d.key === durationKey);
      if (!duration) return null;
      return (family.variants || []).find(
        v =>
          v.durationCount === duration.durationCount &&
          v.durationUnit === duration.durationUnit,
      );
    },
    [],
  );

  const handleFamilyScroll = useCallback(
    event => {
      const offsetX = event.nativeEvent.contentOffset.x;
      const index = Math.round(offsetX / (CARD_WIDTH + CARD_MARGIN * 2));
      if (index !== selectedFamilyIndex && index >= 0 && index < paidPlans.length) {
        setSelectedFamilyIndex(index);
      }
    },
    [selectedFamilyIndex, paidPlans],
  );

  const handleProceed = useCallback(() => {
    const family = paidPlans[selectedFamilyIndex];
    const variant = getVariantForDuration(family, selectedDuration);
    if (!variant) {
      setSnackbar(t('subscription.selectVariant'));
      return;
    }

    navigation.navigate('SubscriptionInvoice', {
      orgUnitId,
      orgUnitName,
      subscriptionId,
      planFamily: family,
      planVariant: variant,
    });
  }, [
    selectedDuration,
    selectedFamilyIndex,
    paidPlans,
    orgUnitId,
    orgUnitName,
    subscriptionId,
    navigation,
    getVariantForDuration,
    t,
  ]);

  const renderFeature = useCallback((feature) => {
    const icon = FEATURE_ICONS[feature.featureCode] || 'check-circle';
    const enabled = feature.isEnabled;

    let label = feature.displayName || feature.featureCode.replace(/_/g, ' ');

    if (feature.limitValue) {
      label += `: ${feature.limitValue}`;
      if (feature.limitUnit) {
        label += ` ${feature.limitUnit.toLowerCase()}`;
      }
    }

    return (
      <View key={feature.id} style={styles.featureRow}>
        <MaterialCommunityIcons
          name={enabled ? icon : 'close-circle-outline'}
          size={18}
          color={enabled ? colors.success : colors.textLight}
        />
        <Text
          style={[
            styles.featureText,
            !enabled && styles.featureDisabled,
          ]}>
          {label}
        </Text>
      </View>
    );
  }, []);

  const renderPlanCard = useCallback(
    ({item: family, index}) => {
      const isSelected = index === selectedFamilyIndex;
      const isPro = family.code === 'PRO';
      const features = family.features || [];
      const variant = getVariantForDuration(family, selectedDuration);
      const hasDiscount =
        variant?.discountPct && parseFloat(variant.discountPct) > 0;

      return (
        <View
          style={[
            styles.planCard,
            isSelected && styles.planCardSelected,
            isPro && styles.planCardPro,
          ]}>
          {/* Plan header with price */}
          <View style={[styles.planHeader, isPro && styles.planHeaderPro]}>
            <View style={styles.planNameRow}>
              <Text style={styles.planName}>{family.name}</Text>
              {isPro && (
                <View style={styles.popularPill}>
                  <MaterialCommunityIcons name="star" size={12} color="#F57C00" />
                  <Text style={styles.popularPillText}>{t('subscription.mostPopular')}</Text>
                </View>
              )}
            </View>
            {variant && (
              <View style={styles.priceContainer}>
                <View style={styles.priceRow}>
                  {hasDiscount && (
                    <Text style={styles.listPrice}>
                      {'\u20B9'}{variant.listPrice}
                    </Text>
                  )}
                  <Text style={styles.netPrice}>
                    {'\u20B9'}{variant.netPrice}
                  </Text>
                </View>
                <Text style={styles.pricePeriod}>
                  {variant.durationCount === 1
                    ? t('subscription.month')
                    : `${variant.durationCount} ${t('subscription.months')}`}
                </Text>
                {hasDiscount && (
                  <View style={styles.discountBadge}>
                    <Text style={styles.discountBadgeText}>
                      {variant.discountPct}% off
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Scrollable features list */}
          <View style={styles.featuresContainer}>
            <Text style={styles.featuresTitle}>{t('subscription.features')}</Text>
            <ScrollView
              style={styles.featuresList}
              showsVerticalScrollIndicator={true}
              nestedScrollEnabled={true}>
              {features.map(renderFeature)}
            </ScrollView>
          </View>
        </View>
      );
    },
    [selectedFamilyIndex, selectedDuration, getVariantForDuration, renderFeature, t],
  );

  if (isLoading) {
    return (
      <View style={styles.container}>
        <GradientHeader title={t('subscription.choosePlan')} onBack={() => navigation.goBack()} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={t('subscription.choosePlan')} onBack={() => navigation.goBack()} />

      {/* Store name banner */}
      {orgUnitName && (
        <View style={styles.storeBanner}>
          <MaterialCommunityIcons name="store" size={18} color={colors.primary} />
          <Text style={styles.storeBannerText}>{orgUnitName}</Text>
        </View>
      )}

      {/* Duration selector at top */}
      <View style={styles.durationBar}>
        {DURATION_OPTIONS.map(opt => {
          const isActive = selectedDuration === opt.key;
          return (
            <TouchableOpacity
              key={opt.key}
              style={[styles.durationChip, isActive && styles.durationChipActive]}
              onPress={() => setSelectedDuration(opt.key)}
              activeOpacity={0.7}>
              <Text
                style={[
                  styles.durationChipText,
                  isActive && styles.durationChipTextActive,
                ]}>
                {opt.durationCount === 1
                  ? t('subscription.monthly')
                  : opt.durationCount === 6
                  ? t('subscription.halfYearly')
                  : t('subscription.yearly')}
              </Text>
              {opt.durationCount === 12 && (
                <View style={styles.bestValueBadge}>
                  <Text style={styles.bestValueText}>{t('subscription.bestValue')}</Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Plan dots indicator */}
      <View style={styles.dotsRow}>
        {paidPlans.map((p, i) => (
          <View
            key={i}
            style={[
              styles.dot,
              i === selectedFamilyIndex && styles.dotActive,
            ]}>
            {i === selectedFamilyIndex && (
              <Text style={styles.dotLabel}>{p.name}</Text>
            )}
          </View>
        ))}
      </View>

      {/* Horizontally scrollable plan cards */}
      <FlatList
        ref={flatListRef}
        data={paidPlans}
        renderItem={renderPlanCard}
        keyExtractor={item => String(item.id)}
        horizontal
        pagingEnabled={false}
        snapToInterval={CARD_WIDTH + CARD_MARGIN * 2}
        snapToAlignment="start"
        decelerationRate="fast"
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={handleFamilyScroll}
        contentContainerStyle={styles.carouselContent}
        style={styles.carousel}
      />

      {/* Subscribe button */}
      <View style={styles.bottomBar}>
        <Button
          mode="contained"
          onPress={handleProceed}
          buttonColor={colors.primary}
          contentStyle={styles.subscribeButtonContent}
          style={styles.subscribeButton}>
          {t('subscription.proceedToInvoice')}
        </Button>
      </View>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},

  storeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  storeBannerText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },

  // Duration selector
  durationBar: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xs,
    gap: spacing.sm,
  },
  durationChip: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  durationChipActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '12',
  },
  durationChipText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  durationChipTextActive: {
    color: colors.primary,
  },
  bestValueBadge: {
    backgroundColor: colors.success,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 1,
    marginTop: 3,
  },
  bestValueText: {
    fontSize: 9,
    fontWeight: '700',
    color: colors.white,
    textTransform: 'uppercase',
  },

  dotsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    gap: 6,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.border,
  },
  dotActive: {
    width: 20,
    backgroundColor: colors.primary,
  },

  carousel: {
    flex: 1,
  },
  carouselContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },

  // Plan card
  planCard: {
    width: CARD_WIDTH,
    marginHorizontal: CARD_MARGIN,
    backgroundColor: colors.surface,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: colors.border,
    overflow: 'hidden',
    flex: 1,
  },
  planCardPro: {
    borderColor: '#F57C00',
    borderWidth: 2,
  },
  planCardSelected: {
    borderColor: colors.primary,
    elevation: 4,
    shadowColor: colors.primary,
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.15,
    shadowRadius: 8,
  },
  planHeader: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
  },
  planHeaderPro: {
    backgroundColor: '#2E7D32',
  },
  planNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  planName: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.white,
  },
  popularPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#FFF3E0',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  popularPillText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#E65100',
  },
  priceContainer: {
    marginTop: spacing.xs,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
  },
  listPrice: {
    fontSize: 14,
    color: colors.white + '80',
    textDecorationLine: 'line-through',
  },
  netPrice: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.white,
  },
  pricePeriod: {
    fontSize: 13,
    color: colors.white + 'BB',
    marginTop: 2,
  },
  discountBadge: {
    alignSelf: 'flex-start',
    backgroundColor: colors.white + '25',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 4,
  },
  discountBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.white,
  },

  // Features
  featuresContainer: {
    flex: 1,
    padding: spacing.md,
  },
  featuresTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  featuresList: {
    flex: 1,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 4,
  },
  featureText: {
    fontSize: 14,
    color: colors.text,
    flex: 1,
  },
  featureDisabled: {
    color: colors.textLight,
    textDecorationLine: 'line-through',
  },

  // Bottom bar
  bottomBar: {
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  subscribeButton: {
    borderRadius: 8,
  },
  subscribeButtonContent: {
    paddingVertical: spacing.xs,
  },
});

export default PlanSelectionScreen;
